import { Component, ErrorHandler, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, signal, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'
import { BicService } from '@lemon/core/service/bic.service'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { Maa, MaaService } from '../../_jaettu-angular/service/maa.service'

import { IbanService } from '@lemon/core/service/iban.service'
import { StringService } from '@lemon/core/service/string.service'
import { LemonaidValidators } from 'app/_angular/_validator/LemonaidValidators'
import { WindowSizeService } from 'app/_jaettu-angular/service/window.service'
import { Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface YrityksenTiedotLomake {
  nimi: string
  ytunnus: string
  katuosoite: string
  postitoimipaikka: string
  postinro: string
  maa: string | null
}

interface YhteystiedotLomake {
  iban: string
  pankki: string
  bic: string
  aspaemail: string
  aspapuh: string
}

@Component({
  templateUrl: './lasku-taydenna-asetukset.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskuTaydennaAsetuksetDialog implements OnInit, OnDestroy {
  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  yrityksenTiedotLomake: FieldTree<YrityksenTiedotLomake> = null
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly yrityksenTiedotErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this._validationService.updateValidationStatus('laskuasetukset', this.yrityksenTiedotLomake, true)
  })
  yhteystiedotLomake: FieldTree<YhteystiedotLomake> = null
  readonly yhteystiedotErrors = computed(() => {
    this._kieli()
    return this._validationService.updateValidationStatus('laskuasetukset', this.yhteystiedotLomake, true)
  })
  // ibanVihje = ''
  readonly mobiili = signal(false)
  maat: Observable<Maa[]>

  private _asetukset: Laskuasetukset = null

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { mobiili: boolean },
    private _dialogRef: MatDialogRef<LaskuTaydennaAsetuksetDialog>,
    private _validationService: FormValidationService,
    private _laskuService: LaskuService,
    private _bicService: BicService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _maaService: MaaService,
    private _stringService: StringService,
    private _windowSizeService: WindowSizeService,
    private _ibanService: IbanService,
  ) {
    this._windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this.mobiili.set(window.innerWidth < 670 || window.innerHeight < 670)
    })

    this.maat = this._maaService.kaikkiMaatObservable
    this.mobiili.set(this.data.mobiili ? true : false)

    // Luo yrityksen tiedot lomake
    this.yrityksenTiedotLomake = form(signal<YrityksenTiedotLomake>({ nimi: '', ytunnus: '', katuosoite: '', postitoimipaikka: '', postinro: '', maa: '' }), polku => {
      required(polku.nimi)
      required(polku.ytunnus)
      required(polku.katuosoite)
      required(polku.postitoimipaikka)
      required(polku.postinro)
      required(polku.maa)
    })

    // Luo yrityksen tiedot lomake
    this.yhteystiedotLomake = form(signal<YhteystiedotLomake>({ iban: '', pankki: '', bic: '', aspaemail: '', aspapuh: '' }), polku => {
      required(polku.iban)
      validoiReaktiivisesti(polku.iban, LemonaidValidators.ibanValidator)
      required(polku.pankki)
      required(polku.bic)
      validoiReaktiivisesti(polku.bic, LemonaidValidators.bicValidator)
      required(polku.aspaemail)
    })
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta).pipe(takeUntil(this._ngUnsubscribe))

    // Yrityksen tiedot
    muutokset(this.yrityksenTiedotLomake.nimi).subscribe(value => {
      this._asetukset.nimi = value
    })
    muutokset(this.yrityksenTiedotLomake.ytunnus).subscribe(value => {
      this._asetukset.ytunnus = value
    })
    muutokset(this.yrityksenTiedotLomake.katuosoite).subscribe(value => {
      this._asetukset.katuosoite = value
    })
    muutokset(this.yrityksenTiedotLomake.postitoimipaikka).subscribe(value => {
      this._asetukset.postitmp = value
    })
    muutokset(this.yrityksenTiedotLomake.postinro).subscribe(value => {
      this._asetukset.postinro = value
    })
    muutokset(this.yrityksenTiedotLomake.maa).subscribe(value => {
      this._asetukset.maakoodi = value
    })

    // Yhteystiedot
    muutokset(this.yhteystiedotLomake.iban).subscribe(value => {
      this._asetukset.iban = this._stringService.removeAllWhiteSpaces(value) || null
      const pankinNimi = this._bicService.annaPankinNimi(this._asetukset.iban)
      const bic = this._bicService.annaBicKoodi(this._asetukset.iban)

      // let pankki = ''
      if (bic) {
        asetaArvo(this.yhteystiedotLomake.bic, bic)
        // pankki += 'BIC: ' + bic
      }
      if (pankinNimi) {
        asetaArvo(this.yhteystiedotLomake.pankki, pankinNimi)
        // pankki += ' (' + pankinNimi + ')'
      }
      // this.ibanVihje = pankki.trim()
    })

    muutokset(this.yhteystiedotLomake.pankki).subscribe(value => {
      this._asetukset.pankki = value?.trim() || null
    })

    muutokset(this.yhteystiedotLomake.bic).subscribe(value => {
      this._asetukset.bic = this._stringService.removeAllWhiteSpaces(value) || null
    })

    muutokset(this.yhteystiedotLomake.aspaemail).subscribe(value => {
      this._asetukset.replyto = value
    })
    muutokset(this.yhteystiedotLomake.aspapuh).subscribe(value => {
      this._asetukset.puhelin = value
    })

    // Load data
    this._laskuService.asetuksetObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asetukset => {
      if (asetukset) {
        this._asetukset = asetukset
      } else {
        this._asetukset = {
          avain: null,
          nimi: null,
          ytunnus: null,
          katuosoite: null,
          postitmp: null,
          postinro: null,
          maakoodi: 'FIN',
          date: null,
          replyto: null,
          puhelin: null,
          bic: null,
          iban: null,
          pankki: null,
          base64Logo: null,
          ohjeet: null,
          slogan: null,
          piilotaYrityksenNimiLaskulla: false,
          summahuomautusPiilotettu: false,
          muistutusSpostiasetukset: {},
          spostiasetukset: {},
          tervetuloaNahty: false,
        }
      }

      this._asetukset.tervetuloaNahty = true

      // Kuuntelijat kopioivat arvot sellaisenaan, joten ilmoittamatta asetuksiin jää null eikä tekstikentän tyhjä merkkijono
      // Yrityksen tiedot
      asetaArvo(this.yrityksenTiedotLomake.nimi, this._asetukset.nimi ?? '', { ilmoita: false })
      asetaArvo(this.yrityksenTiedotLomake.ytunnus, this._asetukset.ytunnus ?? '', { ilmoita: false })
      asetaArvo(this.yrityksenTiedotLomake.katuosoite, this._asetukset.katuosoite ?? '', { ilmoita: false })
      asetaArvo(this.yrityksenTiedotLomake.postitoimipaikka, this._asetukset.postitmp ?? '', { ilmoita: false })
      asetaArvo(this.yrityksenTiedotLomake.postinro, this._asetukset.postinro ?? '', { ilmoita: false })
      asetaArvo(this.yrityksenTiedotLomake.maa, this._asetukset.maakoodi ?? null, { ilmoita: false })

      // Pankkiyhteys
      const trimmed = this._stringService.removeAllWhiteSpaces(this._asetukset.iban || '')
      asetaArvo(this.yhteystiedotLomake.iban, this._ibanService.formatoiIban(trimmed) ?? '')
      asetaArvo(this.yhteystiedotLomake.bic, this._asetukset.bic ?? '')
      asetaArvo(this.yhteystiedotLomake.pankki, this._asetukset.pankki ?? '')

      asetaArvo(this.yhteystiedotLomake.aspaemail, this._asetukset.replyto ?? '', { ilmoita: false })
      asetaArvo(this.yhteystiedotLomake.aspapuh, this._asetukset.puhelin ?? '', { ilmoita: false })
    })
  }

  ngOnInit() {}

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  tallenna() {
    this._ladataanService.aloitaLataaminen()
    this._laskuService
      .updateLaskuasetukset(this._asetukset)
      .then(() => {
        this._ladataanService.lopetaLataaminen()
        this._dialogRef.close('onnistui')
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }
}
